import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Buildings } from 'phosphor-react';

import { IEmpresaOption } from '../../../../hooks/useContaCilImportar';
import {
  IGrupoEmpresas,
  IGrupoEmpresasMembro,
  coberturaCurta,
  dicaDeSomar,
  eAtual,
  ehAtiva,
  papelCurto,
  paraOpcao,
  podeAbrir,
  podeSomar,
  resumoDaSelecao,
  resumoDoGrupo,
  temGrupo,
  temRessalva,
} from './grupoEmpresas';

const COR = {
  ink: '#0F172A',
  muted: '#64748B',
  faint: '#94A3B8',
  brand: '#1B88F5',
  brandSoft: 'rgba(27, 136, 245, 0.10)',
  linha: 'rgba(15, 23, 42, 0.12)',
  warn: '#D97706',
} as const;

interface Props {
  grupo: IGrupoEmpresas;
  /** Trocar de estabelecimento: recebe a MESMA forma que os cards produzem. */
  onTrocar: (opcao: IEmpresaOption) => void;
  /** Ids marcados para SOMAR. A empresa aberta está sempre dentro. */
  selecionados: number[];
  /** Marcar/desmarcar um estabelecimento da soma. */
  onAlternar: (empresaId: number) => void;
}

/** Cor do texto do chip: atual em destaque, abrível em tinta, inerte apagado. */
const corDoChip = (atual: boolean, abre: boolean): string => {
  if (atual) return COR.brand;
  return abre ? COR.ink : COR.faint;
};

/**
 * Barra do grupo matriz/filiais, logo abaixo do nome da empresa selecionada.
 *
 * Diz onde o contador está ("Grupo · matriz + 2 filiais") e lista os
 * estabelecimentos como chips: o atual marcado, os demais clicáveis para trocar
 * sem voltar aos cards. Linha do Acessórias aparece mas não abre (sem código do
 * Domínio não há balancete). O hover de cada chip traz a cobertura — é o que
 * distingue "filial vazia" de "filial não extraída".
 *
 * Cada chip tem uma CAIXINHA que marca o estabelecimento para somar
 * (pedido do usuário, 01/10/2026). Clicar no NOME continua trocando de
 * empresa — são duas ações distintas no mesmo chip, e por isso a caixinha
 * tem área de clique própria e `aria-label` próprio.
 *
 * A soma é SIMPLES: quem exibe o total é obrigado a mostrar os avisos que
 * vêm do backend (duplicidade entre livros, contas intercompany).
 */
export function GrupoEmpresasBar({ grupo, onTrocar, selecionados, onAlternar }: Props) {
  if (!temGrupo(grupo)) return null;

  return (
    <Flex align="center" gap={2} mt={2} wrap="wrap" fontSize="12px">
      <Flex align="center" gap={1} color={COR.muted} fontWeight="600" flexShrink={0}>
        <Buildings size={13} weight="bold" />
        <Text>Grupo · {resumoDoGrupo(grupo)}</Text>
      </Flex>

      {selecionados.length > 1 && (
        <Text color={COR.brand} fontWeight="700" flexShrink={0}>
          {resumoDaSelecao(selecionados)}
        </Text>
      )}

      {grupo.membros.map((m: IGrupoEmpresasMembro) => {
        const atual = eAtual(m, grupo);
        const abre = podeAbrir(m) && !atual;
        const rotulo = `${papelCurto(m)} ${m.external_id ?? '—'}`;
        const marcado = selecionados.includes(m.empresa_id);
        const soma = podeSomar(m);
        return (
          <Flex
            key={m.empresa_id}
            align="center"
            gap={1.5}
            px={2}
            py={0.5}
            borderRadius="full"
            borderWidth="1px"
            borderColor={marcado ? COR.brand : COR.linha}
            bg={marcado ? COR.brandSoft : 'transparent'}
          >
            {/* Caixinha: marca para SOMAR. Desabilitada em membro sem código
                do Domínio (não há balancete) e na empresa aberta (ela é a
                base da tela e o backend a reinjeta). */}
            <Box
              as="input"
              {...{
                type: 'checkbox' as const,
                checked: marcado,
                disabled: !soma || atual,
                onChange: () => onAlternar(m.empresa_id),
                'aria-label': atual
                  ? `${rotulo} — sempre somado (empresa aberta)`
                  : `Somar ${rotulo} ${m.nome ?? ''}`,
                title: dicaDeSomar(soma, atual),
              }}
              cursor={!soma || atual ? 'default' : 'pointer'}
              m={0}
            />
            <Box
              as={abre ? 'button' : 'span'}
              // `type` não existe na tipagem polimórfica do Box; sem ele o button
              // vira type="submit" (mesmo idioma de BalanceteArvore.tsx).
              {...(abre ? { type: 'button' as const } : {})}
              onClick={abre ? () => onTrocar(paraOpcao(m)) : undefined}
              title={`${papelCurto(m)} · ${m.nome ?? ''} · ${coberturaCurta(m)}`}
              aria-label={abre ? `Abrir ${rotulo} ${m.nome ?? ''}` : `${rotulo} ${m.nome ?? ''}`}
              aria-current={atual ? 'true' : undefined}
              color={corDoChip(atual, abre)}
              fontSize="11px"
              fontWeight="600"
              lineHeight="1.4"
              cursor={abre ? 'pointer' : 'default'}
              _hover={abre ? { borderColor: COR.brand } : undefined}
            >
              {rotulo}
              {!ehAtiva(m) && (
                <Text as="span" color={COR.faint} fontWeight="500">
                  {' '}
                  · {m.ativa}
                </Text>
              )}
              {temRessalva(m) && (
                <Text as="span" color={COR.warn} title="Cadastro duplicado sem desempate">
                  {' '}
                  •
                </Text>
              )}
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
}
